// src/components/HeaderTabs.js
import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './Header.module.css';
import SignInForm from './loginforms/SignInForm';
import SignUpForm from './loginforms/SignUpForm';
import HowItWorksContent from './HowItWorksContent';
import VerificationNotice from './loginforms/VerificationNotice'; 
import VerificationPrompt from './loginforms/VerificationPrompt';
import { useAuth } from '../context/AuthProvider';

// Internal component to handle welcome animation and transition
// Inside HeaderTabs.js
const WelcomeContent = () => {
  return (
    <div className={styles.welcomeHero}>
      <div className={styles.welcomeBrand}>
        <img src="/images/linkist-tile.png" alt="Linkist" className={styles.welcomeLogo} />
      </div>

      <h2 className={styles.welcomeHeadline}>One polished profile for everything you share.</h2>
      <p className={styles.welcomeLead}>
        Build a clean link-in-bio page for your links, socials, projects, and updates.
      </p>

      <div className={styles.previewCard} aria-hidden="true">
        <img
          src="/images/linkist-5-profile-louise.png"
          alt=""
          className={styles.previewProfileImage}
        />
        <img
          src="/images/linkist-8-profile-andy.png"
          alt=""
          className={styles.previewProfileImage}
        />
      </div>
    </div>
  );
  
};


const HeaderTabs = ({ activeTab: propActiveTab, setActiveTab, showTabs, setShowTabs, openMenu }) => {
  // If no active tab is passed, default to 'welcome'
  const [localActiveTab, setLocalActiveTab] = useState(propActiveTab || 'welcome');
  const [localShowTabs, setLocalShowTabs] = useState(showTabs);
  
  // Add this new state for animation
  const [isAnimating, setIsAnimating] = useState(false);
  
  // Get location from react-router for handling redirects
  const location = useLocation();
  
  const { 
    verificationPending, 
    verificationEmail, 
    setVerificationPending,
    setVerificationEmail,
    loading,
    showVerificationPrompt
  } = useAuth();

  // Update local state when props change
  useEffect(() => {
    if (propActiveTab) {
      setLocalActiveTab(propActiveTab);
    }
    setLocalShowTabs(showTabs);
  }, [propActiveTab, showTabs]);

  // Update parent state when local state changes
  useEffect(() => {
    setActiveTab(localActiveTab);
  }, [localActiveTab, setActiveTab]);

  useEffect(() => {
    setShowTabs(localShowTabs);
  }, [localShowTabs, setShowTabs]);
  
  // NEW: Handle redirects from ProtectedRoute component
  useEffect(() => {
    // Check if we were redirected from ProtectedRoute with verification needed
    if (location.state && location.state.verificationNeeded) {
      console.log("Redirected from protected route - verification needed");
      setVerificationPending(true);
      setLocalActiveTab('signin'); // Show the sign-in tab but with verification UI
      setLocalShowTabs(true);
      
      if (location.state.verificationEmail) {
        console.log("Setting verification email from redirect:", location.state.verificationEmail);
        setVerificationEmail(location.state.verificationEmail);
      }
    }
  }, [location.state, setVerificationPending, setVerificationEmail]);
  
  // Add this function to handle tab changes with animation
  const changeTab = (newTab) => {
    setIsAnimating(true);
    setTimeout(() => {
      setLocalActiveTab(newTab);
      setLocalShowTabs(true);
      setIsAnimating(false);
    }, 100); // Small delay before actually changing the tab content
  };
  
  // Handle rendering based on verification state
  const renderContent = () => {
    // If verification prompt should be shown, render it with highest priority
    if (showVerificationPrompt) {
      return (
        <div className={styles.tabContainer}>
          <div className={`${styles.tabContent} ${isAnimating ? styles.animating : ''}`}>
            <VerificationPrompt />
          </div>
        </div>
      );
    }
    
    // Then check for verification pending state
    if (verificationPending) {
      return (
        <div className={styles.tabContainer}>
          <div className={styles.tabHeaders}>
            <button
              className={`${styles.tabHeader} ${styles.activeTab}`}
              onClick={() => {
                setVerificationPending(false);
                changeTab('signin');
              }}
              disabled={isAnimating || loading}
            >
              sign in
            </button>
            <button
              className={`${styles.tabHeader}`}
              onClick={() => {
                setVerificationPending(false);
                changeTab('signup');
              }}
              disabled={isAnimating || loading}
            >
              sign up
            </button>
            <button
              className={`${styles.tabHeader}`}
              onClick={() => {
                setVerificationPending(false);
                changeTab('howitworks');
              }}
              disabled={isAnimating || loading}
            >
              how it works
            </button>
          </div>
          
          <div className={`${styles.tabContent} ${isAnimating ? styles.animating : ''}`}>
            <VerificationNotice 
              email={verificationEmail}
              onContinue={() => {
                setVerificationPending(false);
                changeTab('signin');
              }}
            />
          </div>
        </div>
      );
    }

    return (
      <div className={styles.tabContainer}>
        {/* Only show tab headers if not in welcome mode or tabs are explicitly shown */}
        {(localActiveTab !== 'welcome' || localShowTabs) && (
          <div className={styles.tabHeaders}>
            <button
              className={`${styles.tabHeader} ${localActiveTab === 'signin' ? styles.activeTab : ''}`}
              onClick={() => changeTab('signin')}
              disabled={isAnimating || loading}
            >
              sign in
            </button>
            <button
              className={`${styles.tabHeader} ${localActiveTab === 'signup' ? styles.activeTab : ''}`}
              onClick={() => changeTab('signup')}
              disabled={isAnimating || loading}
            >
              sign up
            </button>
            <button
              className={`${styles.tabHeader} ${localActiveTab === 'howitworks' ? styles.activeTab : ''}`}
              onClick={() => changeTab('howitworks')}
              disabled={isAnimating || loading}
            >
              how it works
            </button>
          </div>
        )}

        <div className={`${styles.tabContent} ${isAnimating ? styles.animating : ''}`}>
          {localActiveTab === 'welcome' || !localShowTabs ? (
            <div className={styles.welcomeMessage}>
              <WelcomeContent />
            </div>
          ) : localActiveTab === 'signin' ? (
            <SignInForm />
          ) : localActiveTab === 'signup' ? (
            <SignUpForm setActiveTab={(tab) => changeTab(tab)} />
          ) : (
            <HowItWorksContent 
              goToSignUp={() => changeTab('signup')}
            />
          )}
        </div>
      </div>
    );
  };

  return renderContent();
};

export default HeaderTabs;
